/**
 * PATH: apps/hr/src/pages/clock/ClockPage.tsx
 * FILE_ID: HR-TIMECLOCK-PAGE-001
 * PURPOSE: Time-clock page in apps/hr (Phase 2 of HR Unification).
 *          Clock-in / clock-out screen for field workers, mounted
 *          inside the canonical HR Hub.
 * WHY: Phase 0 + Phase 1 of HR_UNIFICATION_PLAN.md established the
 *      tenant flag (`useHrFeatures().timeclock`) + canonical
 *      `hr.Person`. The MVP UI shipped first in `apps/workforce`
 *      while we proved out the workforce-api wiring; Phase 2 moves
 *      it to its real home in `apps/hr` so the merged HR module is
 *      a single shell. The page is gated on `timeclock` so tenants
 *      without the flag see no UI even on direct URL hit.
 *
 * FLOW:
 *   ┌──────────┐    ┌────────────────┐    ┌────────────────┐
 *   │ on mount │───▶│ GET /punches/  │───▶│ open?          │
 *   └──────────┘    │     current    │    │  yes → ClockOut│
 *                   └────────────────┘    │  no  → ClockIn │
 *                                         └────────────────┘
 *
 * DEPENDENCIES:
 *   - @quotech/auth: useHrFeatures (Phase 0/B)
 *   - apps/hr/src/lib/workforceApi: workforce-api fetch wrapper
 *   - apps/hr/src/lib/setupApi:     shared @quotech/api client (baseURL
 *     `${getApiBase()}/api` → Django paths here omit the `/api` prefix)
 *   - ./clockHelpers / ./clockTypes / ./ClockSubcomponents
 *
 * RELATED:
 *   - workforce-api/app/api/routes/punches.py (BE endpoints)
 *   - apps/workforce/src/pages/ClockPage.tsx (MVP origin — to be
 *     deprecated once apps/hr is the canonical surface).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Clock } from 'lucide-react'
import { useHrFeatures } from '@quotech/auth'
import { workforceApi } from '../../lib/workforceApi'
import { getApiClient, getOrgId } from '../../lib/setupApi'
import {
  extractErrorCode,
  formatDuration,
  getGpsOrNull,
} from './clockHelpers'
import {
  ClockInForm,
  FeatureDisabledNotice,
  GeofenceVerdictBanner,
  SessionDetails,
  StatusBanner,
} from './ClockSubcomponents'
import type {
  ClockInResponse,
  ClockOutResponse,
  CurrentSessionResponse,
  DjangoJobWithPrefs,
  GeofenceStatus,
  JobOption,
  SiteListResponse,
  SiteOption,
} from './clockTypes'
import { useWorkerCategory } from '../../lib/useWorkerCategory'

export default function ClockPage() {
  const { features, isLoading: featuresLoading } = useHrFeatures()

  if (featuresLoading) {
    return <div className="p-8 text-sm text-slate-500">Loading…</div>
  }
  if (!features.timeclock) {
    return <FeatureDisabledNotice />
  }
  return <ClockPanel />
}

interface VerdictState {
  status: GeofenceStatus
  distance_m: number | null
  radius_m: number | null
}

function ClockPanel() {
  const { isOfficeStaff } = useWorkerCategory()
  const [session, setSession] = useState<CurrentSessionResponse | null>(null)
  const [jobs, setJobs] = useState<JobOption[]>([])
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null)
  const [sites, setSites] = useState<SiteOption[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null)
  const [verdict, setVerdict] = useState<VerdictState | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshSession = useCallback(async () => {
    setError(null)
    try {
      const current = await workforceApi.get<CurrentSessionResponse | null>(
        '/punches/current',
      )
      setSession(current ?? null)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(`Failed to load current session: ${msg}`)
    }
  }, [])

  const loadJobs = useCallback(async () => {
    try {
      const orgId = getOrgId()
      const params: Record<string, string> = {
        module: 'workforce',
        limit: '100',
        status: 'active',
      }
      if (orgId) params.tenant = orgId
      const { data } = await getApiClient().get<{ jobs: DjangoJobWithPrefs[] }>(
        '/jobs/with-preferences/',
        { params },
      )
      const opts: JobOption[] = (data?.jobs ?? []).map((j) => ({
        quotech_job_id: j.job_id,
        name: j.name,
        customer_name: j.customer_name,
      }))
      setJobs(opts)
      if (opts.length === 1) setSelectedJobId(opts[0].quotech_job_id)
    } catch {
      // Non-fatal — surface only when the worker tries to clock in
      // without a selection.
      setJobs([])
    }
  }, [])

  const loadSites = useCallback(async () => {
    try {
      const data = await workforceApi.get<SiteListResponse>('/punches/sites')
      // Drop sites without a quotech_site_id since the BE needs it for
      // geofence eval; show the rest.
      const opts = (data?.sites ?? []).filter((s) => s.quotech_site_id !== null)
      setSites(opts)
      if (opts.length === 1) setSelectedSiteId(opts[0].quotech_site_id)
    } catch {
      // Non-fatal — older BE deploys don't have /sites yet. The page
      // still works; the picker just doesn't render.
      setSites([])
    }
  }, [])

  useEffect(() => {
    Promise.all([refreshSession(), loadJobs(), loadSites()]).finally(() =>
      setLoading(false),
    )
  }, [refreshSession, loadJobs, loadSites])

  const handleClockIn = useCallback(async () => {
    if (!selectedJobId) {
      setError('Pick a job first.')
      return
    }
    setBusy(true)
    setError(null)
    setSuccess(null)
    setVerdict(null)
    // Office / management workers are exempt from GPS by policy —
    // the BE stamps office_staff verdict + 1.0 confidence regardless,
    // so the browser geolocation prompt is pure friction here.
    const gps = isOfficeStaff ? null : await getGpsOrNull()
    try {
      const result = await workforceApi.post<ClockInResponse>('/punches/clock-in', {
        quotech_job_id: selectedJobId,
        quotech_site_id: selectedSiteId,
        gps_lat: gps?.lat ?? null,
        gps_lng: gps?.lng ?? null,
      })
      setSuccess('Clocked in.')
      // Stash the geofence verdict so the FE banner renders the
      // right colour/copy. Falls back to 'no_site' for old BEs that
      // haven't deployed the Phase 3 fields yet.
      setVerdict({
        status: result.geofence_status ?? 'no_site',
        distance_m: result.geofence_distance_m ?? null,
        radius_m: result.geofence_radius_m ?? null,
      })
      await refreshSession()
    } catch (err) {
      const code = extractErrorCode(err)
      setError(
        code === 'already_clocked_in'
          ? 'You already have an open session. Refresh to clock out.'
          : `Could not clock in (${code}).`,
      )
    } finally {
      setBusy(false)
    }
  }, [selectedJobId, selectedSiteId, refreshSession, isOfficeStaff])

  const handleClockOut = useCallback(async () => {
    setBusy(true)
    setError(null)
    setSuccess(null)
    setVerdict(null)
    const gps = isOfficeStaff ? null : await getGpsOrNull()
    try {
      const result = await workforceApi.post<ClockOutResponse>(
        '/punches/clock-out',
        {
          gps_lat: gps?.lat ?? null,
          gps_lng: gps?.lng ?? null,
          break_minutes: 0,
        },
      )
      setSuccess(`Clocked out — ${formatDuration(result.duration_minutes)} worked.`)
      await refreshSession()
    } catch (err) {
      const code = extractErrorCode(err)
      setError(
        code === 'not_clocked_in'
          ? 'No active session — refresh to clock in.'
          : `Could not clock out (${code}).`,
      )
    } finally {
      setBusy(false)
    }
  }, [refreshSession, isOfficeStaff])

  const heading = useMemo(
    () => (session ? 'You are clocked in' : 'Clock in'),
    [session],
  )

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading…</div>
  }

  return (
    <div className="max-w-xl mx-auto mt-8 p-6 rounded-lg border border-slate-200 bg-white shadow-sm">
      <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
        <Clock className="w-5 h-5 text-teal-600" />
        {heading}
        {isOfficeStaff && (
          <span className="ml-2 text-xs font-normal px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-100">
            Office mode — no GPS required
          </span>
        )}
      </h2>
      <StatusBanner error={error} success={success} />
      {verdict && (
        <GeofenceVerdictBanner
          status={verdict.status}
          distanceMeters={verdict.distance_m}
          radiusMeters={verdict.radius_m}
        />
      )}
      {session ? (
        <SessionDetails session={session} onClockOut={handleClockOut} busy={busy} />
      ) : (
        <ClockInForm
          jobs={jobs}
          selectedJobId={selectedJobId}
          onSelect={setSelectedJobId}
          sites={sites}
          selectedSiteId={selectedSiteId}
          onSelectSite={setSelectedSiteId}
          onClockIn={handleClockIn}
          busy={busy}
        />
      )}
    </div>
  )
}
